// @spec SHL-STR-049
'use client';

import { createFileRoute } from '@tanstack/react-router';

import { useTranslation } from 'react-i18next';

import { TextScaleControl } from '@/components/accessibility/TextScaleControl';
import { useTheme } from '@/components/theme-provider';
import { ThemeSwitcher } from '@/components/theme-switcher';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/settings/appearance')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.appearance'),
  }),
  component: AppearancePage,
});

/**
 * Appearance settings page for theme and display preferences.
 *
 * Allows users to:
 * - Switch between Light, Dark, and System themes
 * - Adjust text scale for accessibility
 *
 * Settings are persisted to localStorage and apply immediately.
 */
function AppearancePage() {
  const { t } = useTranslation('settings');
  const { theme } = useTheme();
  const isDark =
    theme === 'dark' ||
    (theme === 'system' &&
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches);

  const themeLabel =
    theme === 'system'
      ? t('appearance.system')
      : theme === 'dark'
        ? t('appearance.dark')
        : t('appearance.light');

  return (
    <div className="p-8">
      <div className="mx-auto max-w-2xl space-y-6">
        <header>
          <h1 className="text-3xl font-semibold text-foreground">{t('appearance.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('appearance.subtitle')}
          </p>
        </header>

        <Card>
          <CardHeader>
            <CardTitle>{t('appearance.theme')}</CardTitle>
            <CardDescription>
              {t('appearance.themeDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ThemeSwitcher />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('appearance.textSize')}</CardTitle>
            <CardDescription>
              {t('appearance.textSizeDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <TextScaleControl />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('appearance.preview')}</CardTitle>
            <CardDescription>{t('appearance.previewSubtitle')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg border p-4" role="presentation" aria-label="Theme preview">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{t('appearance.currentTheme')}</span>
                  <span className="text-sm text-muted-foreground">{themeLabel}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{t('appearance.appearanceLabel')}</span>
                  <span className="text-sm text-muted-foreground">{isDark ? t('appearance.dark') : t('appearance.light')}</span>
                </div>
                <hr className="border-border" />
                <div className="space-y-2">
                  <p className="text-sm">
                    {t('appearance.previewText')}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t('appearance.previewSecondary')}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
